import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Switch } from '@/components/ui/switch'
import { AlertCircle, AlertTriangle, Clock } from 'lucide-react'

export const ProductStatusSection = ({
  product,
  authData,
  formValues,
  setValue,
  t,
}) => {
  const isOnboardingIncomplete = authData?.onboarding_step !== 'finished'
  const isDocumentationNotApproved =
    authData?.documentation_status !== 'approved'
  const isProductBlocked = product?.status === 'blocked'
  const isProductAnalysis = product?.status === 'analysis'
  const isProductRevision = product?.status === 'revision'
  const isProductInReview = product?.status === 'in_review'

  const isSwitchDisabled =
    isOnboardingIncomplete ||
    isProductBlocked ||
    isProductAnalysis ||
    isProductRevision ||
    isProductInReview ||
    isDocumentationNotApproved

  const showAccountAlerts = !isProductBlocked

  return (
    <section>
      <article className="flex items-center gap-2">
        <Switch
          id="active"
          checked={formValues?.status === 'active'}
          onCheckedChange={(checked) =>
            setValue('status', checked ? 'active' : 'inactive')
          }
          disabled={isSwitchDisabled}
        />
        <label htmlFor="active" className="text-gray-300 text-sm">
          {t('activate_product')}
        </label>
      </article>

      {isProductBlocked && (
        <Alert variant="destructive" className="mt-2 mb-4 bg-[#211315]">
          <AlertCircle color="#ef4444" className="h-5 w-5 flex-shrink-0" />
          <div>
            <AlertTitle className="text-[#ef4444]">
              {product?.product_reviews?.[0]?.review_title}
            </AlertTitle>
            <AlertDescription className="text-muted-foreground">
              {product?.product_reviews?.[0]?.review_message}
            </AlertDescription>
          </div>
        </Alert>
      )}

      {isProductAnalysis && (
        <Alert variant="default" className="mt-2 mb-4 bg-[#60A5FA1A]">
          <AlertCircle color="#60A5FA" className="h-5 w-5 flex-shrink-0" />
          <div>
            <AlertTitle className="text-[#60A5FA]">
              {t('common:analysis')}
            </AlertTitle>
            <AlertDescription>
              {t('common:analyze_description')}
            </AlertDescription>
          </div>
        </Alert>
      )}

      {isProductRevision && (
        <Alert
          variant="default"
          className="mt-2 mb-4 border-orange-500/30 bg-orange-500/10"
        >
          <AlertTriangle color="#F97316" className="h-5 w-5 flex-shrink-0" />
          <div>
            <AlertTitle className="text-orange-400">
              {product?.product_reviews?.[0]?.review_title}
            </AlertTitle>
            <AlertDescription className="text-muted-foreground">
              {product?.product_reviews?.[0]?.review_message}
            </AlertDescription>
            <AlertDescription className="text-orange-400/80 text-xs mt-2">
              {t('common:revision_action')}
            </AlertDescription>
          </div>
        </Alert>
      )}

      {isProductInReview && (
        <Alert
          variant="default"
          className="mt-2 mb-4 border-violet-500/30 bg-violet-500/10"
        >
          <Clock color="#A78BFA" className="h-5 w-5 flex-shrink-0" />
          <div>
            <AlertTitle className="text-violet-400">
              {t('common:in_review')}
            </AlertTitle>
            <AlertDescription className="text-muted-foreground">
              {t('common:in_review_description')}
            </AlertDescription>
          </div>
        </Alert>
      )}

      {showAccountAlerts && isOnboardingIncomplete && (
        <Alert variant="warning" className="mt-2 mb-4">
          <AlertCircle color="#facc15" className="h-5 w-5 flex-shrink-0" />
          <div>
            <AlertTitle className="text-[#facc15]">
              {t('complete_onboarding_title')}
            </AlertTitle>
            <AlertDescription className="text-muted-foreground">
              {t('complete_onboarding')}
            </AlertDescription>
          </div>
        </Alert>
      )}

      {showAccountAlerts && isDocumentationNotApproved && (
        <Alert variant="warning" className="mt-2 mb-4">
          <AlertCircle color="#facc15" className="h-5 w-5 flex-shrink-0" />
          <div>
            <AlertTitle className="text-[#facc15]">
              {t('documentation_pending_title')}
            </AlertTitle>
            <AlertDescription className="text-muted-foreground">
              {t('onboarding:complete_documentation')}
            </AlertDescription>
          </div>
        </Alert>
      )}
    </section>
  )
}
